// frontend/src/pages/crm/TaskForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { taskAPI, clientAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, PlusIcon, TrashIcon, DocumentDuplicateIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getActiveClientsForTaskAssignment, validateClientForTaskAssignment } from '../../utils/clientValidation';
import { ComboboxSearch } from '../../components/kibo-ui/combobox/';
import { Switch } from '../../components/ui/switch';
import { HelpTooltip } from '../../components/ui/help-components';
import { toast } from 'react-hot-toast';

const TaskForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { branding } = useBranding();
    const isEdit = !!id && id !== 'new';
    const templateId = searchParams.get('template');
    
    // Get client data from navigation state
    const clientData = location.state;

    const [loading, setLoading] = useState(false);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        clientId: '',
        category: 'immigration',
        priority: 'medium',
        status: 'not_started',
        dueDate: '',
        visibleToClient: false,
        steps: [{ title: '', description: '', status: 'todo', order: 0 }]
    });

    useEffect(() => {
        console.log('TaskForm useEffect triggered');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', Object.fromEntries(searchParams.entries()));
        console.log('ClientId from URL:', searchParams.get('clientId'));
        console.log('ID from params:', id);
        console.log('isEdit:', isEdit);
        console.log('templateId:', templateId);
        
        // Early return if ID is 'new' to prevent any task loading
        if (id === 'new') {
            console.log('Early return - ID is "new", skipping task loading');
            loadClients();
            loadTemplates();
            return;
        }
        
        loadClients();
        loadTemplates();
        if (isEdit) {
            console.log('Loading task with ID:', id);
            loadTask();
        } else if (templateId) {
            console.log('Loading template with ID:', templateId);
            loadTemplate(templateId);
        }
        
        // Set clientId from navigation state or URL parameters (fallback)
        const clientIdFromState = clientData?.clientId;
        const clientIdFromQuery = searchParams.get('clientId'); // Fallback for direct URL access
        const clientIdFromUrl = clientIdFromState || clientIdFromQuery;
        
        console.log('ClientId from navigation state:', clientIdFromState);
        console.log('ClientId from query param (fallback):', clientIdFromQuery);
        console.log('Final clientId:', clientIdFromUrl);
        console.log('Client data from state:', clientData);
        
        if (clientIdFromUrl && !isEdit) {
            console.log('Updating formData with clientId:', clientIdFromUrl);
            setFormData(prev => {
                const updated = {
                    ...prev,
                    clientId: clientIdFromUrl
                };
                console.log('Updated formData:', updated);
                return updated;
            });
        }
    }, [id, clientData, templateId, searchParams, isEdit]);

    const loadClients = async () => {
        try {
            setClientsLoading(true);
            const response = await clientAPI.getAll({ limit: 100 });
            if (response.data.success) {
                setClients(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load clients:', error);
        } finally {
            setClientsLoading(false);
        }
    };

    const loadTemplates = async () => {
        try {
            const response = await taskAPI.getTemplates();
            if (response.data.success) {
                setTemplates(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load templates:', error);
        }
    };

    const loadTemplate = async (templateId) => {
        try {
            setLoading(true);
            const response = await taskAPI.getTemplateById(templateId);
            if (response.data.success) {
                const template = response.data.data;
                setSelectedTemplate(template);
                setFormData(prev => {
                    const updated = {
                        title: template.title,
                        clientId: prev.clientId, // Preserve existing clientId
                        category: template.category,
                        priority: 'medium',
                        status: 'not_started',
                        dueDate: '',
                        visibleToClient: false,
                        steps: template.steps?.map(step => ({
                            title: step.title || '',
                            description: step.description || '',
                            ...step,
                            status: 'todo'
                        })) || [{ title: '', description: '', status: 'todo', order: 0 }]
                    };
                    console.log('Template loaded, preserving clientId:', prev.clientId, 'Updated formData:', updated);
                    return updated;
                });
            }
        } catch (error) {
            console.error('Failed to load template:', error);
            toast.error('Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const loadTask = async () => {
        // Safety check: don't load task if ID is 'new' or invalid
        if (!id || id === 'new' || id.length !== 24) {
            console.log('Skipping loadTask - invalid ID:', id);
            return;
        }
        
        // Additional safety check for ObjectId format
        if (!/^[0-9a-fA-F]{24}$/.test(id)) {
            console.log('Skipping loadTask - invalid ObjectId format:', id);
            return;
        }
        
        try {
            setLoading(true);
            console.log('Loading task with ID:', id);
            const response = await taskAPI.getById(id);
            if (response.data.success) {
                const task = response.data.data;
                setFormData({
                    title: task.title || '',
                    clientId: task.clientId?._id || '',
                    category: task.category || 'immigration',
                    priority: task.priority || 'medium',
                    status: task.status || 'not_started',
                    dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
                    visibleToClient: task.visibleToClient || false,
                    steps: task.steps?.length > 0 ? task.steps.map(step => ({
                        title: step.title || '',
                        description: step.description || '',
                        ...step
                    })) : [{ title: '', description: '', status: 'todo', order: 0 }]
                });
            }
        } catch (error) {
            console.error('Failed to load task:', error);
            toast.error('Failed to load task');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStepChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((step, i) => 
                i === index ? { ...step, [field]: value } : step
            )
        }));
    };

    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, { title: '', description: '', status: 'todo', order: prev.steps.length }]
        }));
    };

    const removeStep = (index) => {
        if (formData.steps.length === 1) {
            toast.error('At least one step is required');
            return;
        }
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i }))
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Form submission started');
        console.log('Form data:', formData);
        console.log('Client data from state:', clientData);
        
        // Validation
        if (!formData.title.trim()) {
            toast.error('Task title is required');
            return;
        }

        if (!formData.clientId) {
            console.error('No clientId found in formData:', formData);
            toast.error('Please select a client');
            return;
        }

        // Validate all steps have titles (required by schema)
        const stepsWithoutTitles = formData.steps.filter(s => !s.title || !s.title.trim());
        if (stepsWithoutTitles.length > 0) {
            toast.error('All steps must have a title');
            return;
        }
        
        // Map steps to ensure they have all required fields with proper order
        const validSteps = formData.steps
            .filter(s => s.title && s.title.trim())
            .map((step, index) => ({
                title: step.title.trim(),
                description: step.description || '',
                status: step.status === 'pending' ? 'todo' : (step.status === 'in_progress' ? 'doing' : (step.status || 'todo')),
                order: index
            }));
        
        if (validSteps.length === 0) {
            toast.error('At least one step with title is required');
            return;
        }

        try {
            setLoading(true);
            
            const submitData = {
                ...formData,
                steps: validSteps
            };

            console.log('Submitting task data:', submitData);

            if (isEdit) {
                await taskAPI.update(id, submitData);
                toast.success('Task updated successfully!');
            } else {
                await taskAPI.create(submitData);
                toast.success('Task created successfully!');
            }
            
            // Navigate back to client detail if we came from there, otherwise go to tasks list
            if (clientData?.clientId) {
                console.log('Navigating back to client detail:', clientData.clientId);
                navigate(`/clients/${clientData.clientId}`);
            } else {
                console.log('Navigating to tasks list');
                navigate('/tasks');
            }
        } catch (error) {
            console.error('Failed to save task:', error);
            toast.error(error.response?.data?.message || 'Failed to save task');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/tasks')}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {isEdit ? 'Edit Task' : selectedTemplate ? 'Create Task from Template' : 'Create New Task'}
                            {(clientData?.clientId || searchParams.get('clientId')) && !isEdit && !clientsLoading && (
                                <span className="text-lg font-normal text-muted-foreground ml-2">
                                    for {clientData?.clientName || 
                                         (clients.find(c => c._id === formData.clientId) ? 
                                          (`${clients.find(c => c._id === formData.clientId).firstName || ''} ${clients.find(c => c._id === formData.clientId).lastName || ''}`.trim() || clients.find(c => c._id === formData.clientId).name) : 
                                          'Selected Client')}
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {isEdit ? 'Update task information' : 
                             selectedTemplate ? `Using template: ${selectedTemplate.title}` : 
                             (clientData?.clientId || searchParams.get('clientId')) ? 'Create a new task for this client' : 
                             'Create a task and assign it to a client'}
                        </p>
                    </div>
                </div>
                {!isEdit && !selectedTemplate && (
                    <button
                        type="button"
                        onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                        className="inline-flex items-center px-4 py-2 border rounded-lg transition-colors"
                        style={{
                            borderColor: branding.primaryColor,
                            color: branding.primaryColor
                        }}
                    >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        Use Template
                    </button>
                )}
            </div>

            {/* Template Selector */}
            {showTemplateSelector && !isEdit && !selectedTemplate && (
                <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-primary dark:text-primary/90 mb-3">Select a Template</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {templates.map((template) => (
                            <div
                                key={template._id}
                                onClick={() => {
                                    loadTemplate(template._id);
                                    setShowTemplateSelector(false);
                                }}
                                className="p-4 bg-card border border-border rounded-lg hover:border-primary/50 dark:hover:border-primary/60 hover:shadow-md cursor-pointer transition-all"
                            >
                                <h4 className="font-medium text-foreground mb-1">{template.title}</h4>
                                <p className="text-sm text-muted-foreground capitalize mb-2">{template.category}</p>
                                <p className="text-xs text-muted-foreground">{template.steps?.length || 0} steps</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={() => setShowTemplateSelector(false)}
                            className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Template Info */}
            {selectedTemplate && !isEdit && (
                <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-green-900 dark:text-green-300">Using Template</h3>
                            <p className="text-sm text-green-700 dark:text-green-400">{selectedTemplate.title}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedTemplate(null);
                                setFormData({
                                    title: '',
                                    clientId: '',
                                    category: 'immigration',
                                    priority: 'medium',
                                    status: 'not_started',
                                    dueDate: '',
                                    steps: [{ description: '', status: 'pending', order: 0 }]
                                });
                            }}
                            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors"
                        >
                            Clear Template
                        </button>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-6">
                {/* Basic Information */}
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Task Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Task Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                placeholder="e.g., NIE Application Process"
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            />
                        </div>
                        
                        {/* Only show client selection if not coming from a specific client */}
                        {console.log('Render check - hasClientId:', !!(clientData?.clientId || searchParams.get('clientId')), 'clientsLoading:', clientsLoading, 'formDataClientId:', formData.clientId)}
                        {!(clientData?.clientId || searchParams.get('clientId')) && !clientsLoading && (
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Assign to Client <span className="text-red-500">*</span>
                                </label>
                                <ComboboxSearch
                                    data={[
                                        { value: '', label: 'Select an active client' },
                                        ...getActiveClientsForTaskAssignment(clients).map(client => ({
                                            value: client._id,
                                            label: `${`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown'} (${client.status})`
                                        }))
                                    ]}
                                    value={formData.clientId}
                                    onValueChange={(value) => setFormData(prev => ({
                                        ...prev,
                                        clientId: value
                                    }))}
                                    placeholder="Search and select client..."
                                    className="w-full"
                                />
                                {getActiveClientsForTaskAssignment(clients).length === 0 && (
                                    <p className="mt-2 text-sm text-amber-600">
                                        No active clients available for task assignment. Only clients with "active" status can have tasks assigned.
                                    </p>
                                )}
                                {getActiveClientsForTaskAssignment(clients).length > 0 && clients.length > getActiveClientsForTaskAssignment(clients).length && (
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        Showing {getActiveClientsForTaskAssignment(clients).length} of {clients.length} clients (only active clients can have tasks assigned)
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Show loading state when coming from client view and clients are loading */}
                        {searchParams.get('clientId') && clientsLoading && (
                            <div className="bg-muted border border-border rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-3"></div>
                                    <p className="text-sm text-muted-foreground">Loading client information...</p>
                                </div>
                            </div>
                        )}

                        {/* Show selected client info when coming from client view */}
                        {(clientData?.clientId || searchParams.get('clientId')) && formData.clientId && !clientsLoading && (
                            <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 rounded-full bg-primary/20 dark:bg-primary/30 flex items-center justify-center">
                                            <span className="text-primary dark:text-primary/90 font-medium text-sm">
                                                {clientData?.clientName?.charAt(0) || 
                                                 (clients.find(c => c._id === formData.clientId)?.firstName?.charAt(0)) || 
                                                 'C'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-primary dark:text-primary/90">
                                            {clientData?.clientName || 
                                             (clients.find(c => c._id === formData.clientId) ? 
                                              (`${clients.find(c => c._id === formData.clientId).firstName || ''} ${clients.find(c => c._id === formData.clientId).lastName || ''}`.trim() || clients.find(c => c._id === formData.clientId).name) : 
                                              'Selected Client')}
                                        </p>
                                        <p className="text-xs text-primary/80 dark:text-primary/70">
                                            {clientData?.clientEmail && `Email: ${clientData.clientEmail}`}
                                        </p>
                                        <p className="text-xs text-primary/80 dark:text-primary/70">
                                            Task will be assigned to this client
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Client validation warning */}
                        {(clientData?.clientId || searchParams.get('clientId')) && formData.clientId && !clientsLoading && (() => {
                            const selectedClient = clients.find(c => c._id === formData.clientId);
                            const validation = validateClientForTaskAssignment(selectedClient);
                            
                            // Skip validation if user chose "Proceed Anyway"
                            if (!validation.isValid && !clientData?.bypassValidation) {
                                return (
                                    <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                                                    Cannot Assign Tasks to This Client
                                                </h3>
                                                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                                                    {validation.message}
                                                </p>
                                                <div className="mt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/clients/${formData.clientId}`)}
                                                        className="text-sm font-medium text-red-800 dark:text-red-300 hover:text-red-900 dark:hover:text-red-200 underline"
                                                    >
                                                        Update client status →
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                <option value="immigration">Immigration</option>
                                <option value="relocation">Relocation</option>
                                <option value="housing">Housing</option>
                                <option value="documentation">Documentation</option>
                                <option value="banking">Banking</option>
                                <option value="healthcare">Healthcare</option>
                                <option value="education">Education</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Priority
                            </label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                <option value="not_started">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="blocked">Blocked</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Due Date
                            </label>
                            <input
                                type="date"
                                name="dueDate"
                                value={formData.dueDate}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            />
                        </div>

                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <label htmlFor="visibleToClient" className="block text-sm font-medium text-foreground cursor-pointer">
                                    Visible to Client
                                </label>
                                <HelpTooltip 
                                    content="If selected, this task will appear to clients and account contacts on their portals. If set to false, the task will not be shown on the client portal."
                                    position="top"
                                >
                                    <InformationCircleIcon className="h-4 w-4 text-muted-foreground" />
                                </HelpTooltip>
                            </div>
                            <div className="flex items-center gap-3 px-4 py-2 border border-input rounded-lg bg-background">
                                <span className="text-sm text-muted-foreground flex-1">
                                    Show this task to the client in their portal
                                </span>
                                <Switch
                                    id="visibleToClient"
                                    checked={formData.visibleToClient}
                                    onCheckedChange={(checked) => setFormData(prev => ({
                                        ...prev,
                                        visibleToClient: checked
                                    }))}
                                    style={{
                                        '--tw-ring-color': branding.primaryColor
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-foreground">Task Steps</h3>
                        <button
                            type="button"
                            onClick={addStep}
                            className="inline-flex items-center px-3 py-1 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg text-sm font-medium text-foreground b bg-background text-foregroundg-background hover:bg-muted"
                        >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Step
                        </button>
                    </div>
                    
                    <div className="space-y-3">
                        {formData.steps.map((step, index) => (
                            <div key={index} className="flex items-start space-x-3 p-4 border border-border rounded-lg bg-card">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 dark:bg-primary/30 text-primary dark:text-primary/90 flex items-center justify-center text-sm font-medium">
                                    {index + 1}
                                </div>
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <input
                                            type="text"
                                            value={step.title || ''}
                                            onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                                            placeholder="Step title *"
                                            required
                                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            value={step.description || ''}
                                            onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                            placeholder="Step description (optional)"
                                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                    </div>
                                    <div>
                                        <select
                                            value={step.status}
                                            onChange={(e) => handleStepChange(index, 'status', e.target.value)}
                                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="todo">To Do</option>
                                            <option value="doing">Doing</option>
                                            <option value="done">Done</option>
                                        </select>
                                    </div>
                                </div>
                                {formData.steps.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeStep(index)}
                                        className="flex-shrink-0 text-red-600 hover:text-red-700"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={() => {
                            // Navigate back to client detail if we came from there, otherwise go to tasks list
                            if (clientData?.clientId) {
                                navigate(`/clients/${clientData.clientId}`);
                            } else {
                                navigate('/tasks');
                            }
                        }}
                        className="px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg text-sm font-medium text-foreground b bg-background text-foregroundg-background hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                        style={{
                            backgroundColor: branding.primaryColor,
                            '--tw-ring-color': branding.primaryColor
                        }}
                    >
                        {loading ? 'Saving...' : (isEdit ? 'Update Task' : 'Create Task')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TaskForm;





